import { useMemo } from 'react';
import { ForwardingNumberTypes } from '@frontend/api-forwarding-number';
import { getLocationData } from '@frontend/api-location';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';

const useOutOfScopeLocationNames = (locationIds: string[]) => {
  const { getLocationName } = useAppScopeStore();

  return useQuery({
    queryKey: ['out-of-scope-locations', locationIds.sort()],
    queryFn: async () => {
      const locationPromises = locationIds.map(async (locationId) => {
        try {
          const locationData = await getLocationData({ locationId });
          return { locationId, name: locationData.name || getLocationName(locationId) };
        } catch (error) {
          return { locationId, name: getLocationName(locationId) };
        }
      });

      const results = await Promise.all(locationPromises);
      return Object.fromEntries(results.map(({ locationId, name }) => [locationId, name]));
    },
    enabled: locationIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// This hook resolves location names for forwarding numbers that may reference locations outside the current user's scope/tenant.
interface UseLocationResolverProps {
  allForwardingNumbers: ForwardingNumberTypes.FwdNumberModel[];
  allDevices: any[];
  tenantId?: string;
  enabled?: boolean;
}

interface DeviceLocationResolver {
  getDeviceLocation: (deviceId: string) => string;
  getForwardingNumberLocation: (forwardingNumberId: string) => string;
  isLoading: boolean;
}

export const useLocationResolver = ({
  allForwardingNumbers,
  allDevices,
  enabled = true,
}: UseLocationResolverProps): DeviceLocationResolver => {
  const { getLocationName } = useAppScopeStore();

  // Extract location IDs that need out-of-scope resolution
  const outOfScopeLocationIds = useMemo(() => {
    if (!enabled) return [];

    const locationIds: string[] = [];
    allForwardingNumbers?.forEach((fwdNum) => {
      if (fwdNum.Labels) {
        const locationLabel = fwdNum.Labels.find((label) => label.Name === 'location-id');
        if (locationLabel?.Value && !getLocationName(locationLabel.Value)) {
          locationIds.push(locationLabel.Value);
        }
      }
    });

    return [...new Set(locationIds)]; // Remove duplicates
  }, [allForwardingNumbers, getLocationName, enabled]);

  // Fetch out-of-scope location names using TanStack Query
  const { data: outOfScopeLocations = {}, isLoading } = useOutOfScopeLocationNames(outOfScopeLocationIds);

  // Simplified location name resolver
  const getEnhancedLocationName = (locationId: string): string => {
    // First try the scoped location name resolver
    const scopedName = getLocationName(locationId);
    if (scopedName) return scopedName;

    // Check out-of-scope locations from TanStack Query
    return outOfScopeLocations[locationId] || getLocationName(locationId);
  };
  const getDeviceLocation = (deviceId: string): string => {
    if (!enabled || !deviceId) return '';

    const matchingDevice = allDevices.find((d) => d.deviceId === deviceId);
    return matchingDevice?.location?.name || '';
  };

  // Forwarding number location resolver (unchanged logic)
  const getForwardingNumberLocation = (forwardingNumberId: string): string => {
    if (!enabled || !forwardingNumberId) return '';

    const forwardingNumber = allForwardingNumbers.find((fwdNum) => fwdNum.id === forwardingNumberId);

    if (forwardingNumber?.Labels) {
      const locationLabel = forwardingNumber.Labels.find((label) => label.Name === 'location-id');

      if (locationLabel?.Value) {
        const locationName = getEnhancedLocationName(locationLabel.Value);
        return locationName || `Location ${locationLabel.Value}`;
      }
    }

    return '';
  };

  return {
    getDeviceLocation,
    getForwardingNumberLocation,
    isLoading,
  };
};
